import React, { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpText } from '../../../components/HelpText';
import { PreviewFile } from '../types/PreviewFile';
import { PreviewFiles } from './PreviewFiles';

interface FilesUploaderProps {
    testid: string;
    handleChange: (files: File[]) => void;
}

const MAXIMUM_FILE_SIZE_IN_MB = 50 * 1024 * 1024;

export const FilesUploader: React.FC<FilesUploaderProps> = ({
    testid,
    handleChange,
}) => {
    const [files, setFiles] = useState<PreviewFile[]>([]);
    const [error, setError] = useState<string | undefined>();
    const { t } = useTranslation();

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles) return;
        for (const file of selectedFiles) {
            if (file.size > MAXIMUM_FILE_SIZE_IN_MB) {
                setError(`${file.name} ${t('file excedeed 50MB')}`);
                return;
            }
        }
        setError(undefined);

        const fileArray = Array.from(selectedFiles);
        const imagePreviews = fileArray.map((file) => ({
            file,
            url: URL.createObjectURL(file),
        }));

        setFiles((prev) => [...prev, ...imagePreviews]);
        handleChange(imagePreviews.map((imagePreview) => imagePreview.file));
        event.target.value = ''; // reset input for allowing to re add a deleted file
    };

    const handleRemove = useCallback((index: number) => {
        setFiles((prev) => {
            const newFiles = [...prev];
            URL.revokeObjectURL(newFiles[index].url);
            newFiles.splice(index, 1);
            return newFiles;
        });
    }, []);
    const inputFileId = 'file-upload-input';
    const hasError = error !== undefined;
    return (
        <div className="form-group">
            <label>{t('Add files')}</label>
            <div>
                <input
                    type="file"
                    className="form-control-file d-none"
                    multiple
                    accept="image/*, .dicom, .dcm, .pdf, .doc, .docx"
                    onChange={handleFileChange}
                    id={inputFileId}
                    data-testid={testid}
                />
            </div>
            <button
                onClick={() => document.getElementById(inputFileId)?.click()}
                type="button"
                className="btn btn-secondary"
                style={{ marginBottom: '12px' }}
            >
                <i className="fa fa-file-import"></i> {t('Add attachments')}
            </button>
            {hasError ? (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            ) : (
                <></>
            )}
            <PreviewFiles files={files} handleRemove={handleRemove} />
            <HelpText style={{ marginTop: '0px' }}>
                <>
                    {t(
                        'Attach any files that could help us such as X-rays, implant labels, screenshots, etc... Accepted formats: png, jpg, bmp, tiff, dicom, pdf, word. Max 50MB.'
                    )}
                </>
            </HelpText>
        </div>
    );
};
